import { useState, useEffect } from "react";

export const AlertText = ({
  title,
  message,
  variant = "info",
  position = "top-center",
  duration = 4000,
  action,
  onRemove,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-remove after duration (100ms early to avoid progress bar reset)
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, duration - 150);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const handleRemove = () => {
    setIsLeaving(true);
    setTimeout(() => {
      if (onRemove) {
        onRemove();
      } else {
        // If no onRemove callback, remove the component from DOM
        setIsRemoved(true);
      }
    }, 300); // Match animation duration
  };

  const getIcon = () => {
    switch (variant) {
      case "success":
        return "bi-check-circle-fill";
      case "error":
        return "bi-x-circle-fill";
      case "warning":
        return "bi-exclamation-triangle-fill";
      case "info":
      default:
        return "bi-info-circle-fill";
    }
  };

  const positionClass =
    {
      "top-right": "position-fixed top-0 end-0 pt-4 pe-3",
      "top-left": "position-fixed top-0 start-0 pt-4 ps-3",
      "bottom-right": "position-fixed bottom-0 end-0 pb-4 pe-3",
      "bottom-left": "position-fixed bottom-0 start-0 pb-4 ps-3",
      "top-center": "position-fixed top-0 start-50 translate-middle-x pt-4",
    }[position] || "position-fixed top-0 end-0 pt-4 pe-3";

  // Don't render if component has been removed
  if (isRemoved) {
    return null;
  }

  return (
    <div className={`toast-container ${positionClass} z-3`}>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className={`toast-item card position-relative overflow-hidden border-0 p-3 shadow-sm position-${position} ${
          isVisible && !isLeaving ? "toast-enter" : "to-assert"
        } ${isLeaving ? "toast-exit" : "assertive"}`}
      >
        <div className="d-flex align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center align-items-start gap-2">
            <div className="toast-icon">
              <i className={`bi ${getIcon()} text-${variant}`}></i>
            </div>
            <div className="toast-body">
              {title && <div className="h6">{title}</div>}
              <div className="text-muted">{message}</div>
              {action && (
                <button className="toast-action" onClick={action.onClick}>
                  {action.label}
                </button>
              )}
            </div>
          </div>
          <button
            className="d-flex align-items-center justify-content-center btn btn-transparent h-min-auto py-0 px-1"
            onClick={handleRemove}
            aria-label="Close notification"
          >
            <i className="bi bi-x text-muted"></i>
          </button>
        </div>
        {duration > 0 && (
          <div
            className={`toast-progress bg-${variant} bg-opacity-75`}
            style={{ animationDuration: `${duration}ms` }}
          />
        )}
      </div>
    </div>
  );
};
